import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Clock, Calendar as CalendarIcon, Video, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BookingCalendar from '../components/booking/BookingCalendar';
import TimeSlots from '../components/booking/TimeSlots';
import BookingForm from '../components/booking/BookingForm';
import Confirmation from '../components/booking/Confirmation';
import { bookingApi } from '../lib/bookingApi';
import { formatLongDate } from '../lib/bookingTime';

export default function BookPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null); // {connected, owner}
  const [step, setStep] = useState('pick'); // pick | form | done
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [submittedForm, setSubmittedForm] = useState(null);
  const [idemKey] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));

  const [calendarBroken, setCalendarBroken] = useState(false);

  const oauthNotice = params.get('calendar');
  const oauthReason = params.get('reason');

  useEffect(() => {
    document.title = 'Book a Free Discovery Call | Founder Growth Collective';
    bookingApi.status().then(setStatus).catch(() => setStatus({ connected: false }));
  }, []);

  const fetchAvailability = useCallback(async (date) => {
    setLoadingSlots(true);
    setSlotsError(null);
    try {
      const data = await bookingApi.availability(date);
      if (!data.connected) {
        setSlots([]);
        setSlotsError('Calendar is being set up. Please try again shortly.');
        setCalendarBroken(true);
      } else {
        setSlots(data.slots || []);
        setCalendarBroken(false);
      }
    } catch (e) {
      setSlotsError('Could not load availability. Please try again.');
      // 503 = not connected; 502 = google unreachable; 500 = token revoked
      const s = e?.response?.status;
      if (s === 503 || s === 502 || s === 500) setCalendarBroken(true);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) fetchAvailability(selectedDate);
  }, [selectedDate, fetchAvailability]);

  const onPickDate = (d) => {
    setSelectedSlot(null);
    setSelectedDate(d);
  };

  const onPickSlot = (s) => setSelectedSlot(s);

  const onContinue = () => {
    if (selectedSlot) setStep('form');
  };

  const doSubmit = async (form) => {
    setSubmitting(true);
    setFormError(null);
    try {
      const res = await bookingApi.book({ ...form, start_iso: selectedSlot.start_iso, idempotency_key: idemKey });
      setBooking(res);
      setSubmittedForm(form);
      setStep('done');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      setFormError(detail);
      if (e?.response?.status === 409) {
        // slot was taken — back to slots and refresh
        setStep('pick');
        setSelectedSlot(null);
        if (selectedDate) fetchAvailability(selectedDate);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen">
      <Navbar />

      <main className="pt-36 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">

          {oauthNotice === 'connected' && (
            <div className="mb-8 border border-[#B5876C]/40 bg-[#F3EEE7] px-5 py-3 text-sm text-[#2E2C28]">
              Calendar connected successfully.
            </div>
          )}
          {oauthNotice === 'error' && (
            <div className="mb-8 border border-[#e3b5a8] bg-[#f4dcd3] px-5 py-3 text-sm text-[#8a3a2e] inline-flex items-center gap-2">
              <AlertTriangle size={14} /> Calendar connection failed ({oauthReason || 'unknown'}).
            </div>
          )}

          {(status && !status.connected) || calendarBroken ? (
            <NotConnectedNotice />
          ) : null}

          {step !== 'done' ? (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              {/* LEFT — info */}
              <aside className="lg:col-span-4">
                <p className="sec-num text-sm tracking-[0.3em] uppercase mb-6">Book</p>
                <h1 className="font-serif text-[40px] lg:text-[54px] leading-[1.02] tracking-[-0.015em] text-[#2E2C28] font-light">
                  Book your <span className="italic text-[#B5876C]">Free Discovery Call</span>.
                </h1>

                <div className="mt-10 space-y-6">
                  <MetaRow icon={<Clock size={16} strokeWidth={1.5} />} title="30 minutes" sub="Free discovery call." />
                  <MetaRow icon={<AlertTriangle size={16} strokeWidth={1.5} />} title="24 hour notice required" sub="Please book at least 24 hours in advance." />
                  <MetaRow icon={<CalendarIcon size={16} strokeWidth={1.5} />} title="Availability" sub="9:00 AM – 6:00 PM UK Time" />
                  <MetaRow icon={<Video size={16} strokeWidth={1.5} />} title="Google Meet" sub="A Google Meet link will be included with the booking." />
                </div>

                {selectedDate && selectedSlot && (
                  <div className="mt-12 hidden lg:block border-l-2 border-[#B5876C] pl-5">
                    <p className="text-[11px] tracking-[0.24em] uppercase text-[#7a7369]">Selected</p>
                    <p className="font-serif italic text-lg text-[#2E2C28] mt-2 leading-snug">{formatLongDate(selectedDate)}<br />{selectedSlot.label} UK Time</p>
                  </div>
                )}

                <Link to="/" className="inline-block mt-14 text-[12px] tracking-[0.2em] uppercase border-b border-[#3f3c37] pb-1 hover:text-[#B5876C] hover:border-[#B5876C] transition-colors">← Back to site</Link>
              </aside>

              {/* RIGHT — booking area */}
              <section className="lg:col-span-8">
                {step === 'pick' && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-7">
                      <p className="text-[11px] tracking-[0.24em] uppercase text-[#7a7369] mb-4">Step 1 · Select a date</p>
                      <BookingCalendar selectedDate={selectedDate} onSelect={onPickDate} />
                    </div>
                    <div className="lg:col-span-5">
                      <p className="text-[11px] tracking-[0.24em] uppercase text-[#7a7369] mb-4">Step 2 · Select a time</p>
                      {!selectedDate ? (
                        <div className="border border-[#d9d2ca] p-6 text-center">
                          <p className="font-serif italic text-lg text-[#57534b]">Select a date to view times.</p>
                        </div>
                      ) : (
                        <TimeSlots
                          loading={loadingSlots}
                          slots={slots}
                          selected={selectedSlot}
                          onSelect={onPickSlot}
                          error={slotsError}
                          onRetry={() => fetchAvailability(selectedDate)}
                        />
                      )}

                      {selectedSlot && (
                        <button onClick={onContinue} data-testid="continue-btn" className="mt-6 w-full bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] h-12 text-[12px] tracking-[0.2em] uppercase font-medium transition-colors">Continue →</button>
                      )}
                    </div>
                  </div>
                )}

                {step === 'form' && selectedSlot && (
                  <BookingForm
                    date={selectedDate}
                    slot={selectedSlot}
                    onBack={() => setStep('pick')}
                    onSubmit={doSubmit}
                    submitting={submitting}
                    error={formError}
                  />
                )}
              </section>
            </div>
          ) : (
            <Confirmation booking={booking} date={selectedDate} slot={selectedSlot} form={submittedForm} />
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function MetaRow({ icon, title, sub }) {
  return (
    <div className="flex items-start gap-4">
      <span className="w-9 h-9 border border-[#B5876C]/40 flex items-center justify-center text-[#B5876C] shrink-0 mt-0.5">{icon}</span>
      <div>
        <p className="text-sm text-[#2E2C28] font-medium">{title}</p>
        <p className="text-sm text-[#57534b] font-light mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function NotConnectedNotice() {
  const [connecting, setConnecting] = useState(false);
  const go = async () => {
    setConnecting(true);
    try {
      const { authorization_url } = await bookingApi.login();
      window.location.href = authorization_url;
    } catch { setConnecting(false); }
  };
  return (
    <div className="mb-10 border border-[#B5876C]/40 bg-[#F3EEE7] p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <p className="font-serif text-lg text-[#2E2C28]">Calendar needs reconnecting</p>
        <p className="text-sm text-[#57534b] mt-1">The Google Calendar link for hannah@foundergrowthcollective.co.uk has expired or been revoked. Reconnect to restore real availability.</p>
      </div>
      <button onClick={go} disabled={connecting} data-testid="connect-calendar-btn" className="bg-[#2E2C28] hover:bg-[#B5876C] text-[#EBE4DE] h-12 px-6 text-[12px] tracking-[0.2em] uppercase disabled:opacity-60">
        {connecting ? 'Redirecting…' : 'Reconnect Google Calendar'}
      </button>
    </div>
  );
}
